import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

export const TermsOfServicePage = () => {
  const lastUpdated = "December 2025";
  
  return (
    <div data-testid="terms-of-service-page" className="bg-white">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-500 hover:text-teal-600 mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
            
            <motion.div variants={itemVariants} className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-teal-100 border border-teal-200 flex items-center justify-center">
                <FileText size={28} className="text-teal-600" />
              </div>
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-slate-500 block">
                  Legal
                </span>
                <h1 className="font-heading text-4xl font-bold text-slate-900">
                  Terms of Service
                </h1>
              </div>
            </motion.div>
            
            <motion.p variants={itemVariants} className="text-slate-500">
              Last Updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-main">
          <div className="max-w-4xl mx-auto prose prose-slate prose-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg">
                <p className="text-slate-700 m-0">
                  Welcome to Health Strategy and Leadership ("HSL"). By accessing or using our website 
                  and services, you agree to be bound by these Terms of Service. Please read them carefully 
                  before using our services.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900 mt-0">1. Acceptance of Terms</h2>
                <p className="text-slate-600">
                  By accessing and using the HSL website (www.hsl.com) and any services offered through it, 
                  you accept and agree to be bound by these Terms of Service and our Privacy Policy. If you 
                  do not agree to these terms, please do not use our website or services.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">2. Services Description</h2>
                <p className="text-slate-600">
                  HSL provides professional consulting services in health strategy, leadership development, 
                  policy innovation, and health systems strengthening. Our website provides information about 
                  our services, resources, job opportunities, and a platform for inquiries and applications.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">3. Use of Website</h2>
                <p className="text-slate-600">You agree to use our website only for lawful purposes and in accordance with these Terms. You agree not to:</p>
                <ul className="text-slate-600">
                  <li>Use the website in any way that violates applicable laws or regulations</li>
                  <li>Attempt to gain unauthorized access to any portion of the website or its systems</li>
                  <li>Use any automated means to access the website or collect information</li>
                  <li>Interfere with the proper functioning of the website</li>
                  <li>Transmit any malicious code or harmful content</li>
                  <li>Impersonate any person or entity or misrepresent your affiliation</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">4. Intellectual Property</h2>
                <p className="text-slate-600">
                  All content on this website, including but not limited to text, graphics, logos, images, 
                  audio clips, digital downloads, data compilations, and software, is the property of HSL or 
                  its content suppliers and is protected by international copyright laws.
                </p>
                <p className="text-slate-600">
                  The HSL name, logo, and all related names, logos, product and service names, designs, and 
                  slogans are trademarks of HSL. You may not use such marks without our prior written permission.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">5. User Submissions</h2>
                <p className="text-slate-600">
                  By submitting any content to us through our contact forms, job applications, or other means, 
                  you grant HSL a non-exclusive, royalty-free, perpetual right to use, reproduce, modify, and 
                  distribute such content for the purposes for which it was submitted.
                </p>
                <p className="text-slate-600">
                  You represent and warrant that you own or have the necessary rights to submit such content 
                  and that the content does not infringe upon the rights of any third party.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">6. Professional Services</h2>
                <p className="text-slate-600">
                  Any engagement for professional consulting services will be governed by a separate written 
                  agreement between HSL and the client. The information provided on this website is for 
                  informational purposes only and does not constitute professional advice.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">7. Job Applications</h2>
                <p className="text-slate-600">
                  By submitting a job application through our website, you acknowledge that:
                </p>
                <ul className="text-slate-600">
                  <li>All information provided is accurate and complete</li>
                  <li>HSL may verify the information you provide</li>
                  <li>Submission does not guarantee employment</li>
                  <li>Your application will be processed in accordance with our Privacy Policy</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">8. Third-Party Links</h2>
                <p className="text-slate-600">
                  Our website may contain links to third-party websites that are not owned or controlled by HSL. 
                  We have no control over, and assume no responsibility for, the content, privacy policies, or 
                  practices of any third-party websites. Your use of third-party websites is at your own risk.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">9. Disclaimer of Warranties</h2>
                <p className="text-slate-600">
                  THE WEBSITE AND ITS CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES 
                  OF ANY KIND, EITHER EXPRESS OR IMPLIED. HSL DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT 
                  LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND 
                  NON-INFRINGEMENT.
                </p>
                <p className="text-slate-600">
                  HSL does not warrant that the website will be uninterrupted, secure, or error-free, or that 
                  any defects will be corrected.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">10. Limitation of Liability</h2>
                <p className="text-slate-600">
                  TO THE FULLEST EXTENT PERMITTED BY LAW, HSL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                  SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER 
                  INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">11. Indemnification</h2>
                <p className="text-slate-600">
                  You agree to defend, indemnify, and hold harmless HSL and its officers, directors, employees, 
                  and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, 
                  or expenses arising out of or relating to your violation of these Terms or your use of the website.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">12. Governing Law</h2>
                <p className="text-slate-600">
                  These Terms shall be governed by and construed in accordance with the laws of the Federal 
                  Republic of Nigeria, without regard to its conflict of law provisions. Any disputes arising 
                  from these Terms shall be resolved in the courts of Nigeria.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">13. Changes to Terms</h2>
                <p className="text-slate-600">
                  We reserve the right to modify these Terms at any time. We will notify users of any material 
                  changes by posting the new Terms on this page and updating the "Last Updated" date. Your 
                  continued use of the website after any changes constitutes acceptance of the new Terms.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">14. Severability</h2>
                <p className="text-slate-600">
                  If any provision of these Terms is held to be invalid, illegal, or unenforceable, such 
                  provision shall be modified to the minimum extent necessary to make it valid and enforceable, 
                  and the remaining provisions shall continue in full force and effect.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 p-6 rounded-lg">
                <h2 className="font-heading text-2xl text-slate-900 mt-0">15. Contact Information</h2>
                <p className="text-slate-600 mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="flex items-center gap-3 text-slate-700">
                  <Mail size={20} className="text-teal-600" />
                  <a href="mailto:info@hsl.com" className="text-teal-600 hover:text-teal-700">
                    info@hsl.com
                  </a>
                </div>
                <p className="text-slate-600 mt-4 mb-0">
                  Health Strategy and Leadership<br />
                  No 4, Natasha Apoti Street, Kado District<br />
                  Abuja, Nigeria
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
